import { useState, useEffect, useCallback, useRef } from 'react';
import './LockScreen.css';

const PIN_LENGTH = 4;

// Face ID 실행 중 visibilitychange로 재잠금 방지용 전역 플래그
let bioInProgress = false;
export function isBioInProgress() { return bioInProgress; }

function hashPin(pin) {
  let hash = 0;
  for (let i = 0; i < pin.length; i++) {
    hash = ((hash << 5) - hash + pin.charCodeAt(i)) | 0;
  }
  return String(hash);
}

async function tryBiometric() {
  if (!window.PublicKeyCredential) return false;

  try {
    const credId = localStorage.getItem('lifelog_lock_bio_cred');
    if (!credId) return false;

    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const idBytes = Uint8Array.from(atob(credId), c => c.charCodeAt(0));

    await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ id: idBytes, type: 'public-key', transports: ['internal'] }],
        timeout: 60000,
        userVerification: 'required',
      },
    });
    return true;
  } catch {
    return false;
  }
}

export default function LockScreen({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shaking, setShaking] = useState(false);
  const bioEnabled = localStorage.getItem('lifelog_lock_bio') === 'true';
  const bioAttempted = useRef(false);

  const handleUnlock = useCallback((inputPin) => {
    const savedHash = localStorage.getItem('lifelog_lock_pin');
    if (hashPin(inputPin) === savedHash) {
      onUnlock();
    } else {
      setError('비밀번호가 틀렸습니다');
      setShaking(true);
      setTimeout(() => { setShaking(false); setPin(''); setError(''); }, 600);
    }
  }, [onUnlock]);

  const handleKey = useCallback((num) => {
    setError('');
    const next = pin + num;
    setPin(next);
    if (next.length === PIN_LENGTH) {
      setTimeout(() => handleUnlock(next), 100);
    }
  }, [pin, handleUnlock]);

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleBio = useCallback(async () => {
    bioInProgress = true;
    const ok = await tryBiometric();
    bioInProgress = false;
    if (ok) onUnlock();
  }, [onUnlock]);

  // 마운트 시 자동 생체인증 — 1회만 실행
  useEffect(() => {
    if (bioEnabled && !bioAttempted.current) {
      bioAttempted.current = true;
      handleBio();
    }
  }, [bioEnabled, handleBio]);

  return (
    <div className="lock-screen">
      <div className="lock-icon">🔒</div>
      <p className="lock-title">잠금 해제</p>
      <p className="lock-subtitle">비밀번호를 입력하세요</p>

      <div className={`lock-dots${shaking ? ' lock-dots--shake' : ''}`}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span key={i} className={`lock-dot${i < pin.length ? ' lock-dot--filled' : ''}`} />
        ))}
      </div>

      {error && <p className="lock-error">{error}</p>}

      <div className="lock-keypad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} className="lock-key" onClick={() => handleKey(String(n))}>
            {n}
          </button>
        ))}
        <button
          className="lock-key lock-key--bio"
          onClick={handleBio}
          disabled={!bioEnabled}
          style={{ visibility: bioEnabled ? 'visible' : 'hidden' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 11c0-1.1.9-2 2-2s2 .9 2 2v1" />
            <path d="M8 15c0-2.2 1.8-4 4-4s4 1.8 4 4" />
            <path d="M2 12a10 10 0 0 1 20 0" />
            <path d="M12 15v4" />
            <path d="M6 12a6 6 0 0 1 12 0" />
          </svg>
        </button>
        <button className="lock-key" onClick={() => handleKey('0')}>0</button>
        <button className="lock-key lock-key--del" onClick={handleDelete}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
            <line x1="18" y1="9" x2="12" y2="15" />
            <line x1="12" y1="9" x2="18" y2="15" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export { hashPin, tryBiometric };
